import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 6
};

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const BinForm = NiceModal.create(({ id, mode, modalName }) => {
	const axios = useAxiosPrivate();
	const { closeModal } = useModalRouter();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();

	const [isMinimizing, setIsMinimizing] = useState(false);

	const viewMode = useMemo(() => mode === 'view', [mode]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				warehouseID: yup.number().nullable().required(),
				binName: yup.string().nullable().required(),
				zone: yup.string().nullable(),
				aisle: yup.string().nullable(),
				rack: yup.string().nullable(),
				shelf: yup.string().nullable(),
				itemCountCap: yup.number().nullable(),
				binType: yup.string().nullable().required(),
				preferredSKU: yup.string().nullable(),
				binResponsibleUserIDs: yup.array().of(yup.number().nullable()).nullable()
			}),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			warehouseID: null,
			binName: null,
			zone: null,
			aisle: null,
			rack: null,
			shelf: null,
			itemCountCap: null,
			binType: 'adjustment',
			preferredSKU: null,
			binResponsibleUserIDs: []
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const { data } = await axios.post(ADVANCE_WMS_MODULE.GET_BIN_BY_ID, { id });

			return {
				...defaultValues,
				...data,
				binResponsibleUserIDs: data.binResponsibleUserIDs.map((item) => item.userID)
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});
	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !viewMode && !isMinimizing);

	const onSubmit = async (data) => {
		try {
			const URL = id ? ADVANCE_WMS_MODULE.UPDATE_BIN : ADVANCE_WMS_MODULE.ADD_BIN;
			if (!Array.isArray(data.binResponsibleUserIDs)) {
				data.binResponsibleUserIDs = [];
			}
			const { data: responseData, status } = await axios.post(URL, data);
			if (status === 200) {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.BINS));
				closeModal();
			} else {
				throw new Error(responseData);
			}
		} catch (error) {
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
			AppLogger.error(error);
		}
	};

	const getTitle = useMemo(() => (mode === 'edit' ? 'editBin' : mode === 'view' ? 'viewBin' : 'addBin'), [mode]);
	const getSubmitLabel = useMemo(
		() => (mode === 'edit' ? 'save' : mode === 'view' ? 'button.view' : 'button.add'),
		[mode]
	);

	return (
		<Modal
			name={modalName}
			maxWidth="medium"
			fullwidth
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={getTitle}
					tooltip={['edit', 'view'].includes(mode) ? watch('binName') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{getTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="warehouse" isLoading={isLoading} required>
						<AutoComplete
							name="warehouseID"
							disabled={viewMode}
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							setValue={(value) => {
								if (watch('warehouseID') !== value?.ID)
									setValue('warehouseID', value?.ID ?? null, validateOptions);
							}}
							value={watch('warehouseID')}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="binName" isLoading={isLoading} required>
						<StringInput disabled={viewMode} name="binName" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="zone" isLoading={isLoading}>
						<StringInput disabled={viewMode} name="zone" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="aisle" isLoading={isLoading}>
						<StringInput disabled={viewMode} name="aisle" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="rack" isLoading={isLoading}>
						<StringInput disabled={viewMode} name="rack" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="shelf" isLoading={isLoading}>
						<StringInput disabled={viewMode} name="shelf" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="itemCountCap" isLoading={isLoading}>
						<IntegerInput disabled={viewMode} name="itemCountCap" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="binType" isLoading={isLoading} required>
						<EnumSelect
							name="binType"
							placeholder="binType"
							enumName={ENUMS.BIN_TYPE}
							disabled={viewMode}
							setValue={(value) => {
								if (watch('binType') !== value) setValue('binType', value ?? null, validateOptions);
							}}
							value={watch('binType')}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="preferedSku" isLoading={isLoading}>
						<AutoComplete
							name="preferredSKU"
							disabled={viewMode}
							autoCompleteName={AUTOCOMPLETE.PRODUCTS}
							setValue={(value) => {
								if (watch('preferredSKU') !== value?.ID)
									setValue('preferredSKU', value?.ID ?? null, validateOptions);
							}}
							value={watch('preferredSKU')}
							columns={[{ accessor: 'SKU', width: 200 }]}
							selectionLabelAccessor={['SKU']}
						/>
					</FormItem>
					<FormItem colSpan={{ md: 12 }} label="binResponsibles" isLoading={isLoading}>
						<AutoComplete
							name="binResponsibleUserIDs"
							disabled={viewMode}
							autoCompleteName={AUTOCOMPLETE.OTO_PACK_USERS}
							setValue={(value) => {
								if (
									JSON.stringify(watch('binResponsibleUserIDs')) !==
									JSON.stringify(value?.map((item) => item.ID ?? null))
								)
									setValue(
										'binResponsibleUserIDs',
										value?.map((item) => item.ID ?? null),
										validateOptions
									);
							}}
							value={watch('binResponsibleUserIDs')}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
							multipleSelection
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={closeModal} />
				{mode !== 'view' && (
					<Button
						label={getSubmitLabel}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						loading={isLoading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});
export default BinForm;
